'use client';

import type { CSSProperties } from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { useMarketingBannerOffset } from 'components/shared/Header/MarketingBanner';
import { getSpeedsizeImageUrl } from 'components/shared/custom-image/CustomImage';
import { campaignHeroCtaProps } from './v3/homepageCta';
import v3 from './v3/v3.module.css';
import styles from './TwoHourDeliveryHero.module.css';

const ART_BASE = 'web_v2/homepage-v3/banners';

const DEFAULT_ART = {
  desktop: getSpeedsizeImageUrl(
    `${ART_BASE}/two-hour-delivery/desktop/two-hr-banner.png`,
    1920,
  ),
  mobile: getSpeedsizeImageUrl(
    `${ART_BASE}/two-hour-delivery/mobile/two-hour-banner.png`,
    800,
  ),
};

// Figma UH-Landing-Page 1877:10050 / 1877:10137, exported at 4x.
const CAMPAIGN_ART = {
  desktop: getSpeedsizeImageUrl(`${ART_BASE}/desktop/banner-6.png`, 1920),
  mobile: getSpeedsizeImageUrl(`${ART_BASE}/mobile/banner-6.png`, 800),
};

export type TwoHourHeroCampaign = {
  /** Percentage off Ring AIR, from the live ring campaign. */
  discountPercent: number;
};

type TwoHourDeliveryHeroProps = {
  /** True when HeroCarousel already owns header clearance above this card. */
  stackedBelowCarousel?: boolean;
  /** Set while an IN ring campaign runs: campaign art and "{n}% off" headline. */
  campaign?: TwoHourHeroCampaign;
};

export const TwoHourDeliveryHero = ({
  stackedBelowCarousel = false,
  campaign,
}: TwoHourDeliveryHeroProps) => {
  const t = useTranslations('homepage');
  const bannerPx = useMarketingBannerOffset();
  const firstFold = !stackedBelowCarousel;
  const art = campaign ? CAMPAIGN_ART : DEFAULT_ART;

  return (
    <section
      className={`${styles.section} ${
        stackedBelowCarousel ? styles.stacked : ''
      }`}
      aria-label={
        campaign
          ? t('homepage.twoHourHero.campaign.aria.section')
          : t('homepage.twoHourHero.aria.section')
      }
      data-testid="two-hour-delivery-hero"
      data-variant={campaign ? 'campaign' : undefined}
      data-header-theme="light"
      style={
        {
          '--campaign-banner-px': `${bannerPx}px`,
          '--frost-photo': `url("${art.mobile}")`,
        } as CSSProperties
      }
    >
      <picture>
        <source media="(max-width: 899px)" srcSet={art.mobile} />
        <img
          src={art.desktop}
          alt=""
          className={styles.bg}
          fetchPriority={firstFold ? 'high' : undefined}
          loading={firstFold ? undefined : 'lazy'}
          decoding="async"
        />
      </picture>
      <div className={styles.overlay} aria-hidden />
      <div className={styles.bottomScrim} aria-hidden />
      <div className={styles.copy}>
        <div className={styles.titleGroup}>
          <p className={styles.eyebrow}>{t('homepage.twoHourHero.eyebrow')}</p>
          <h2 className={`${v3.campaignDisplay} ${styles.title}`}>
            {campaign
              ? t('homepage.twoHourHero.campaign.titleLine1', {
                  discount: campaign.discountPercent,
                })
              : t('homepage.twoHourHero.titleLine1')}
            <br />
            {campaign
              ? t('homepage.twoHourHero.campaign.titleLine2')
              : t('homepage.twoHourHero.titleLine2')}
          </h2>
          <p className={styles.subtitle}>
            {campaign
              ? t('homepage.twoHourHero.campaign.subtitle')
              : t('homepage.twoHourHero.subtitle')}
          </p>
        </div>
        <CustomLink
          href="/ring/buy/"
          className={`${v3.pill} ${styles.cta}`}
          {...campaignHeroCtaProps('ring')}
        >
          {t('homepage.cta.buyNow')}
        </CustomLink>
      </div>
    </section>
  );
};
